/**
 * Schedule View — the visit checklist's task lines (Phase 10d).
 *
 * Plan: docs/plans/2026-10-02-schedule-view-checklist-table-plan.md ("10d. Task lines")
 *
 * `watchTasks` (src/store/scheduleView.js) already listens to the tasks that tag the day's patients
 * to draw the `task` icon. This file turns the SAME snapshot into the per-patient list the
 * checklist prints — one `{ id, text, dueAt, who, assignees }` line per open task — so the two can
 * never disagree: `lines[p].length === counts[p]` for every patient, by construction. `who` is the
 * assignees' names; `assignees` (EmployeeIDs) is kept so the names can be re-resolved when the
 * employee roster arrives late (nameTaskLines).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE. cypress/node-tests/schedule-view-task-lines.test.js executes the
 * real file verbatim. Everything that would need an import (the HTML cleaner, the timezone-aware
 * date, the employee names) is INJECTED through `opts`, the same pattern as `describe` in
 * checklist.js.
 *
 * ⛔ Task TEXT is allowed in exactly one place: these lines, for the visit checklist. Firestore
 * bypasses the PHI demo mask, and a task routinely names a patient. So a line reads only the four
 * fields the checklist prints — content, due date, assignees — and never subtasks, comments, tags
 * or wiki pages, which are free text nobody here has vetted.
 */

/** The longest task text a line carries. The checklist row is a sentence, not the task body. */
export const TASK_TEXT_MAX = 160;

/**
 * The values to put in the `array-contains-any` query, as NUMBERS.
 *
 * ⛔ Firestore array matching is TYPE-STRICT, and `tasks.taggedPatients` holds numbers (measured on
 * dev: 316 of 316 elements were numbers). The same query that returned 41 task documents with the
 * number returned 0 with the string. `watchTasks` used to `.map(String)` the day's PatNums, so the
 * `task` icon never fired on real data — and every unit test passed, because they mock Firestore
 * and a mock does not care what type it is asked about.
 *
 * Accepts numbers or all-digit strings (the page keys its patients as strings), drops anything else
 * rather than guessing, and removes duplicates. Never returns a string.
 */
export function taskQueryValues(patNums) {
  const list = Array.isArray(patNums) ? patNums : [];
  const seen = Object.create(null);
  const out = [];
  list.forEach((p) => {
    let n = null;
    if (typeof p === 'number') n = p;
    else if (typeof p === 'string' && /^\s*[0-9]+\s*$/.test(p)) n = Number(p);
    if (n === null || !Number.isSafeInteger(n) || n <= 0) return;
    if (seen[n]) return;
    seen[n] = true;
    out.push(n);
  });
  return out;
}

/** "MM/DD/YYYY ..." → 'YYYY-MM-DD' for ordering, or null. By hand: no Date, no moment. */
function dueDay(dueAt) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(dueAt || ''));
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const pad = n => (n < 10 ? `0${n}` : String(n));
  return `${m[3]}-${pad(mm)}-${pad(dd)}`;
}

/** The text of a task, cleaned and cut to TASK_TEXT_MAX (the cut ends in an ellipsis). */
function lineText(content, clean) {
  let text = '';
  if (typeof clean === 'function') {
    try {
      const cleaned = clean(content);
      text = (cleaned === undefined || cleaned === null) ? '' : String(cleaned).trim();
    } catch (e) {
      text = '';
    }
  }
  if (text.length <= TASK_TEXT_MAX) return text;
  let cut = text.slice(0, TASK_TEXT_MAX - 1);
  // A cut through the middle of an emoji leaves half a surrogate pair, which renders as a box.
  const last = cut.charCodeAt(cut.length - 1);
  if (last >= 0xD800 && last <= 0xDBFF) cut = cut.slice(0, -1);
  return `${cut.replace(/\s+$/, '')}…`;
}

/** Every assignee: `assignees[]` ∪ `assignedToEmpID`, deduped as strings, order kept. */
function assigneeList(data) {
  const out = [];
  // A null-prototype map, here and in the fold: on a plain `{}`, `seen['constructor']` is already
  // truthy, so an id (or a stray tag) spelled like an Object property would be treated as seen.
  const seen = Object.create(null);
  const add = (id) => {
    if (id === undefined || id === null || id === '') return;
    const key = String(id);
    if (seen[key]) return;
    seen[key] = true;
    out.push(key);
  };
  (Array.isArray(data.assignees) ? data.assignees : []).forEach(add);
  add(data.assignedToEmpID);
  return out;
}

/**
 * EmployeeIDs → "Name, Name": the ONE rule for turning assignees into the checklist's "Assigned
 * to" text, shared by buildTaskLine (at fold time) and nameTaskLines (at read time) so the two
 * cannot drift. An id with no name is left out, never shown raw; none → ''.
 */
function namesFor(ids, employeeNames) {
  const names = (employeeNames && typeof employeeNames === 'object') ? employeeNames : {};
  return (Array.isArray(ids) ? ids : [])
    // hasOwnProperty, not `names[id]`: an id spelled "constructor" must not find Object's own.
    .filter(empId => Object.prototype.hasOwnProperty.call(names, empId) && names[empId])
    .map(empId => String(names[empId]))
    .join(', ');
}

/**
 * One checklist line for one task document, or null when there is no id to key it by.
 *
 *   opts.clean         (html) => text      — stripMentions; strips the patient badge spans too
 *   opts.dueAtOf        (data) => 'MM/DD/YYYY ...' — the due DAY in the OFFICE's timezone
 *   opts.employeeNames  { '<EmployeeID>': 'Name' } — an id with no name here is left out, not shown raw
 *
 * ⛔ Reads ONLY `content`, `dueAt` (through dueAtOf), `assignees` and `assignedToEmpID`. `dueAtStr`
 * is deliberately not read: it turns up in three different formats in real data.
 */
export function buildTaskLine(id, data, opts) {
  if (id === undefined || id === null || id === '') return null;
  const d = (data && typeof data === 'object') ? data : {};
  const o = opts || {};

  let dueAt = '';
  if (typeof o.dueAtOf === 'function') {
    try {
      const due = o.dueAtOf(d);
      if (typeof due === 'string') dueAt = due;
    } catch (e) {
      dueAt = '';
    }
  }

  // `assignees` rides on the line so `who` can be re-resolved later, when the roster has arrived
  // (see nameTaskLines). `who` is still resolved here too: correct whenever the roster is already in.
  const assignees = assigneeList(d);
  return {
    id: String(id), text: lineText(d.content, o.clean), dueAt, who: namesFor(assignees, o.employeeNames), assignees,
  };
}

/**
 * One listener's snapshot → the counts and lines for the patients IN ITS CHUNK.
 *
 *   docs   [{ id, data }]
 *   chunk  the values that listener queried with (numbers; compared as strings)
 *
 * Both rules the listener always had are kept here:
 *   • a COMPLETED task is skipped — it is history, and an icon for it would never clear;
 *   • only patients in THIS chunk are counted. A task may tag several patients, including ones not
 *     on today's schedule, and counting those would invent icons for people who are not on screen
 *     (and double-count across chunks).
 *
 * A task that tags two of the day's patients is listed under both. A patient tagged TWICE on one
 * task (two badges for the same person in the text) is counted ONCE: one task is one line, and two
 * lines with the same id would collide on the checklist's tick key.
 *
 * Keys are STRINGS, matching the alerts payload's `byPatNum`.
 */
export function foldTaskSnapshot(docs, chunk, opts) {
  const inChunk = Object.create(null);
  (Array.isArray(chunk) ? chunk : []).forEach((p) => { inChunk[String(p)] = true; });

  const counts = {};
  const lines = {};

  (Array.isArray(docs) ? docs : []).forEach((entry) => {
    const data = (entry && entry.data && typeof entry.data === 'object') ? entry.data : {};
    if (data.status && String(data.status).toLowerCase() === 'completed') return;

    const seen = Object.create(null);
    const patients = [];
    (Array.isArray(data.taggedPatients) ? data.taggedPatients : []).forEach((p) => {
      const key = String(p);
      if (!inChunk[key] || seen[key]) return;
      seen[key] = true;
      patients.push(key);
    });
    if (!patients.length) return;

    // No id, no line, no count: counts and lines are one list, so they cannot drift apart. A
    // Firestore document always has an id; this only guards a malformed caller.
    const line = buildTaskLine(entry && entry.id, data, opts);
    if (!line) return;

    patients.forEach((key) => {
      counts[key] = (counts[key] || 0) + 1;
      if (!lines[key]) lines[key] = [];
      lines[key].push(Object.assign({}, line, { assignees: line.assignees.slice() }));
    });
  });

  return { counts, lines };
}

/** Soonest due first, undated last, ties by id — the same order on every render. */
function byDueThenId(a, b) {
  const da = dueDay(a.dueAt);
  const db = dueDay(b.dueAt);
  if (da !== db) {
    if (da === null) return 1;
    if (db === null) return -1;
    return da < db ? -1 : 1;
  }
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * Every chunk's result → the one `{ counts, lines }` the store commits.
 *
 * Each listener fires on its own and knows nothing about the other chunks' patients, so the store
 * keeps a result per chunk and combines them here. A chunk that has not answered yet is `{}` (or
 * missing) and simply contributes nothing.
 */
export function combineTaskChunks(results) {
  const counts = {};
  const lines = {};

  (Array.isArray(results) ? results : []).forEach((result) => {
    if (!result || typeof result !== 'object') return;
    const c = result.counts || {};
    const l = result.lines || {};
    Object.keys(c).forEach((p) => { counts[p] = (counts[p] || 0) + (Number(c[p]) || 0); });
    Object.keys(l).forEach((p) => { lines[p] = (lines[p] || []).concat(l[p] || []); });
  });

  Object.keys(lines).forEach((p) => { lines[p].sort(byDueThenId); });
  return { counts, lines };
}

/**
 * The lines with `who` re-resolved against the roster AS IT IS NOW.
 *
 * ⛔ Why this exists: a line's `who` is frozen when its snapshot is folded, and the employee list
 * can arrive AFTER that. `customer.employees` comes in the slow startup batch (with the patient
 * roster) while Schedule View's day answers in about a second, so on a hard refresh the listener
 * can take its first snapshot with no employees at all. Every line then said "" — which the
 * checklist prints as "Unassigned" for a task somebody IS assigned to — and it stayed wrong until
 * that chunk's listener happened to fire again. Re-naming at read time makes the names follow the
 * roster.
 *
 * Pure: returns a NEW object with the same keys and order, every line a copy; the input is Vuex
 * state and is never touched. A line without an `assignees` array keeps the `who` it has. The
 * id → names rule is namesFor, shared with buildTaskLine.
 */
export function nameTaskLines(linesByPatNum, employeeNames) {
  const out = {};
  if (!linesByPatNum || typeof linesByPatNum !== 'object') return out;
  Object.keys(linesByPatNum).forEach((patNum) => {
    const list = linesByPatNum[patNum];
    out[patNum] = (Array.isArray(list) ? list : []).map((line) => {
      if (!line || typeof line !== 'object') return line;
      if (!Array.isArray(line.assignees)) return Object.assign({}, line);
      return Object.assign({}, line, {
        who: namesFor(line.assignees, employeeNames),
        assignees: line.assignees.slice(),
      });
    });
  });
  return out;
}
